import { api } from './api';
import type {
  FoldersResponse,
  ReviewFolder,
  SavedGame,
  GameNote,
  GameReviewState,
  SavedGamesResponse,
  FolderFilter,
} from '../types/review';

// Same rule as puzzle-api: everything goes through `api` (services/api.ts), never
// a bare fetch(), so the Authorization header and the 401 → refresh → retry
// interceptor apply. All endpoints require a registered (non-guest) account.

export async function fetchFolders(): Promise<FoldersResponse> {
  return api.get<FoldersResponse>('/review/folders');
}

export async function createFolder(name: string): Promise<ReviewFolder> {
  const { folder } = await api.post<{ folder: ReviewFolder }>('/review/folders', { name });
  return folder;
}

export async function renameFolder(folderId: string, name: string): Promise<ReviewFolder> {
  const { folder } = await api.patch<{ folder: ReviewFolder }>(
    `/review/folders/${encodeURIComponent(folderId)}`,
    { name },
  );
  return folder;
}

export async function deleteFolder(folderId: string): Promise<void> {
  await api.del(`/review/folders/${encodeURIComponent(folderId)}`);
}

export async function fetchSavedGames(options: {
  folderId?: FolderFilter;
  variant?: string;
  timeControl?: string;
  /** Viewer-relative: saved games you didn't play drop out when this is set. */
  result?: 'win' | 'loss' | 'draw';
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<SavedGamesResponse> {
  const params = new URLSearchParams();
  if (options.folderId) params.set('folderId', options.folderId);
  if (options.variant) params.set('variant', options.variant);
  if (options.timeControl) params.set('timeControl', options.timeControl);
  if (options.result) params.set('result', options.result);
  if (options.limit) params.set('limit', String(options.limit));
  if (options.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  return api.get<SavedGamesResponse>(`/review/games${qs ? `?${qs}` : ''}`, { signal: options.signal });
}

/** Saved state + notes for one game, loaded when the replay screen opens. */
export async function fetchGameReview(gameId: string): Promise<GameReviewState> {
  return api.get<GameReviewState>(`/review/games/${encodeURIComponent(gameId)}`);
}

/** Idempotent: also the "move to another folder" call. `null` = no folder. */
export async function saveGameToFolder(gameId: string, folderId: string | null): Promise<SavedGame> {
  const { saved } = await api.put<{ saved: SavedGame }>(
    `/review/games/${encodeURIComponent(gameId)}/save`,
    { folderId },
  );
  return saved;
}

export async function unsaveGame(gameId: string): Promise<void> {
  await api.del(`/review/games/${encodeURIComponent(gameId)}/save`);
}

/**
 * Writes the note at `ply`. Blank text deletes it server-side and resolves to
 * null, so the caller can drop the entry from its local map either way.
 */
export async function upsertNote(gameId: string, ply: number, text: string): Promise<GameNote | null> {
  const res = await api.put<{ note?: GameNote }>(
    `/review/games/${encodeURIComponent(gameId)}/notes/${ply}`,
    { text },
  );
  return res?.note ?? null;
}

export async function deleteNote(gameId: string, ply: number): Promise<void> {
  await api.del(`/review/games/${encodeURIComponent(gameId)}/notes/${ply}`);
}
